import { useEffect, useMemo, useState } from "react";

/** COMPONENT */
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import TableCustom from "../../../component/table/tableCustom";
import TableRowCommon from "../../../component/table/TableRowCommon";
import { notiError } from "../../../component/notification/notifications";

/** API */
import DepositRefundApi, { DepositRefundReceiptRow } from "../../../api/deposit-refund";

/** UTILS */
import { colors } from "../../../constants/colors";
import { DEPOSIT_REFUND_RECEIPT_LIMIT } from "../../../constants/depositRefund";
import { getApiErrorStatus } from "../../../utils/api-error.utils";
import { getBranch } from "../../../utils/app.utils";
import { DASH, dateOnlyToView, getDepositRefundError } from "../depositRefund.utils";
import { downloadDepositRefundReceipt } from "../depositRefundFile.utils";

type Props = {
  /** ผู้เช่าเจ้าของคำร้อง — ใบเสร็จของคนอื่นไม่เกี่ยวกับการพิจารณาใบนี้ */
  userId: number;
  /**
   * แผงของคำร้อง — เป็น null ได้เมื่อสัญญายกเลิก/โอนสิทธิแล้วและ backend ไล่หาแผงไม่เจอ
   * null = ไม่ส่ง stallId ไป จะได้ใบเสร็จทุกแผงของผู้เช่ารายนี้ ดีกว่าได้ตารางว่าง
   */
  stallId?: number | null;
  heading?: string;
};

/** คอลัมน์ปุ่มดาวน์โหลด — ไม่ส่ง width มา TableRowCommon จะบังคับ 160 ให้ cell แบบ COMPONENT */
const ACTION_COL_WIDTH = 90;

/**
 * ตาราง "ใบเสร็จรับเงิน" แบบอ่านอย่างเดียว ใช้ใน modal พิจารณา + modal ประวัติการพิจารณา
 * (เคสอนุมัติ) ให้เจ้าหน้าที่เปิดดูใบเสร็จของผู้เช่าได้โดยไม่ต้องออกไปหน้าใบแจ้งหนี้
 *
 * โหลดข้อมูลเองในคอมโพเนนต์ เพราะสองโมดัลที่เรียกใช้ต้องการชุดเดียวกันเป๊ะ
 * ถ้าให้ผู้เรียกโหลดมาส่ง จะกลายเป็นโค้ดโหลดข้อมูลซ้ำสองที่ที่ต้องแก้พร้อมกันทุกครั้ง
 *
 * เส้นนี้เป็นของฝั่งเจ้าหน้าที่เท่านั้น (role CUSTOMER ได้ 403) หน้าฝั่งผู้เช่าจึงต้องไม่เรียก
 */
export default function ReceiptTable({
  userId,
  stallId,
  heading = "ใบเสร็จรับเงินที่เกี่ยวข้อง",
}: Props) {
  const [rows, setRows] = useState<DepositRefundReceiptRow[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /** แถวที่กำลังดาวน์โหลด — เก็บเป็น key ที่รวม source กัน refId ซ้ำกันข้ามที่มา */
  const [busyKey, setBusyKey] = useState<string>("");

  /**
   * สาขาที่เลือกไว้ตอน login — เส้นนี้ไม่ scope สาขาจาก token
   * ไม่ส่งจะเห็นใบเสร็จข้ามสาขา (เหมือนเส้นอื่นในโมดูลนี้ ดู useDepositRefundFilter)
   */
  const branchId = useMemo(() => {
    const value = Number(getBranch());
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }, []);

  useEffect(() => {
    /** กัน setState หลัง unmount ตอนผู้ใช้ปิด modal ก่อน response กลับมา */
    let isMounted = true;

    const loadReceipt = async () => {
      setIsLoading(true);
      try {
        const res = await DepositRefundApi.receipt({
          branchId,
          userId,
          // ส่งเฉพาะตอนมีค่า ไม่งั้น stallId = 0 จะกลายเป็นตัวกรองที่ไม่มีแผงไหนตรง
          ...(stallId ? { stallId } : {}),
          page: 1,
          limit: DEPOSIT_REFUND_RECEIPT_LIMIT,
        });
        if (!isMounted) return;
        const data = res.data?.data;
        setRows(Array.isArray(data?.items) ? data.items : []);
        setTotalItems(Number(data?.meta?.totalItems) || 0);
      } catch (error) {
        if (!isMounted) return;
        setRows([]);
        setTotalItems(0);
        notiError(getDepositRefundError(error, "ดึงรายการใบเสร็จไม่สำเร็จ"));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadReceipt();
    return () => {
      isMounted = false;
    };
  }, [branchId, userId, stallId]);

  /** refId ซ้ำกันข้าม source ได้ (invoiceId 45 กับ depositId 45) จึงต้องรวม source ทุกครั้ง */
  const rowKey = (row: DepositRefundReceiptRow) => `${row.source}-${row.refId}`;

  const handleDownload = async (row: DepositRefundReceiptRow) => {
    if (busyKey) return;

    setBusyKey(rowKey(row));
    try {
      await downloadDepositRefundReceipt(row);
    } catch (error) {
      // 404 ของ backend ตอบ body ว่าง จึงต้องดูจาก status แล้วใส่ข้อความเอง
      const status = getApiErrorStatus(error);
      notiError(
        status === 404
          ? "ไม่พบใบเสร็จของเอกสารนี้"
          : getDepositRefundError(error, "ดาวน์โหลดใบเสร็จไม่สำเร็จ")
      );
    } finally {
      setBusyKey("");
    }
  };

  const headCells = [
    {
      id: "documentNo",
      disablePadding: false,
      label: "เลขที่คำร้อง/เลขที่ใบแจ้งหนี้",
      align: "left",
    },
    { id: "tenantName", disablePadding: false, label: "ผู้เช่าแผงค้า", align: "left" },
    { id: "zoneName", disablePadding: false, label: "โซน", align: "left" },
    { id: "stallNumber", disablePadding: false, label: "เลขที่แผง", align: "center" },
    {
      id: "action",
      disablePadding: false,
      label: "Action",
      align: "center",
      width: ACTION_COL_WIDTH,
    },
  ];

  const renderData = (objData: DepositRefundReceiptRow, no: number) => {
    const key = rowKey(objData);
    const isDownloading = busyKey === key;

    const objRenderData = {
      key: `${key}-${no}`,
      id: key,
      obj: objData,
      columns: [
        {
          option: "COMPONENT",
          align: "left",
          component: (
            // source_name เป็นคำไทยจาก backend บอกที่มาได้ตรงๆ ไม่ต้องเพิ่มคอลัมน์
            <Tooltip
              title={[objData.source_name, dateOnlyToView(objData.document_date)]
                .filter(Boolean)
                .join(" · ")}
            >
              <span>{objData.document_no || DASH}</span>
            </Tooltip>
          ),
        },
        { option: "TEXT", align: "left", label: objData.tenant_name || DASH },
        { option: "TEXT", align: "left", label: objData.zone_name || DASH },
        { option: "TEXT", align: "center", label: objData.stall_number || DASH },
        {
          option: "COMPONENT",
          align: "center",
          width: ACTION_COL_WIDTH,
          component: (
            <Tooltip title="ดาวน์โหลดใบเสร็จ">
              <span>
                <IconButton
                  size="small"
                  disabled={!!busyKey}
                  onClick={() => handleDownload(objData)}
                >
                  {isDownloading ? (
                    <CircularProgress size={16} sx={{ color: colors.themeMainColor }} />
                  ) : (
                    <DownloadOutlinedIcon sx={{ fontSize: 20, color: colors.themeMainColor }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ),
        },
      ],
    };

    return <TableRowCommon {...objRenderData} />;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: 16 }}>{heading}</Typography>
        {/* ดึงมาทีเดียวไม่มี pagination — ถ้าของจริงเกินเพดาน ต้องบอกผู้ใช้ว่าเห็นไม่ครบ */}
        {totalItems > rows.length && (
          <Typography sx={{ fontSize: 14, color: colors.textLightGray }}>
            {`แสดง ${rows.length} จาก ${totalItems} รายการ`}
          </Typography>
        )}
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
            borderRadius: "8px",
            border: `1px solid ${colors.lightGray}`,
            backgroundColor: colors.white,
          }}
        >
          <CircularProgress size={28} sx={{ color: colors.themeMainColor }} />
        </Box>
      ) : (
        <TableCustom
          headCells={headCells}
          customScroll
          hidePagination
          page={1}
          // ห้ามเป็น 0 เด็ดขาด — Math.ceil(count / 0) = Infinity แล้ว renderPage วนไม่จบ แท็บค้าง
          pageLimit={rows.length || 1}
          sortType="ASC"
          sortBy=""
          rowCount={rows.length}
          rowsData={rows.map((row, index) => renderData(row, index))}
          onSort={() => {}}
          setPage={() => {}}
          setPageLimit={() => {}}
          textEmptyData="ไม่พบใบเสร็จของผู้เช่ารายนี้"
          tableMinWidth={720}
          // stickyHeader ต้องใช้คู่กับ tableMaxHeight ไม่งั้นไม่มีผล (MUI ต้องการ container สูงจำกัด)
          // และถ้าไม่ส่ง tableMaxHeight ตารางจะได้ minHeight calc(100% - 65px) ซึ่งระเบิดใน modal
          tableMaxHeight={280}
          stickyHeader
        />
      )}
    </Box>
  );
}
